// third party imports
import { useState, useEffect, useContext } from "react";
import { message } from "antd";

// internal rebet imports
import "./stylesPersonalLimitsPopup.css";

// component imports
import EditTimeLimitsPopup from "./EditTimeLimitsPopup/EditTimeLimitsPopup";
import EditCurrencyLimitsPopup from "./EditCurrencyLimitsPopup/EditCurrencyLimitsPopup";
import EditWaggerLimitsPopup from "./EditWaggerLimitsPopup/EditWaggerLimitsPopup";
import EditLossLimitsPopup from "./EditLossLimitsPopup/EditLossLimitsPopup";
import PersonalLimits from "./PersonalLimits/PersonalLimits";
import createPrivateAdminPanelInstance from "../../../../../api/PrivateAdminPanelInstance";
import { API_ROUTES } from "../../../../../api/ApiRoutes";
import EditCasinoLimitsPopup from "./EditCasinoLimitsPopup/EditCasinoLimitsPopup";
import CasinoLimits from "./CasinoLimits/CasinoLimits";
import SportsbookLimits from "./SportsbookLimits/SportsbookLimits";
import ToggleSwitch from "../../../../CommonComponents/ToggleSwitch/ToggleSwitch";
import { AppStrings } from "../../../../../utils/constants/AppStrings";
import { formatString } from "../../../../../utils/constants/stringUtils";
import { CloseOutlined } from "@ant-design/icons";
import { useGetWithdrawPrevention } from "../../../../../queries/user-card/useGetWithdrawPrevention";
import { usePatchWithdrawPrevention } from "../../../../../queries/user-card/usePatchWithdrawPrevention";
import { useGetBonusEnabled } from "../../../../../queries/user-card/useGetBonusEnabled";
import { usePatchBonusEnabled } from "../../../../../queries/user-card/usePatchBonusEnabled";
import { useGetReferrerBonusEnabled } from "../../../../../queries/user-card/useGetReferrerBonusEnabled";
import { usePatchReferrerBonusEnabled } from "../../../../../queries/user-card/usePatchReferrerBonusEnabled";
import AppContext from "../../../../../context/AppContext";
// asset imports


const buildReferrerBonusToast = (response) => {
  // Deliberately ignores `msg_code`: those are BE i18n catalog keys and
  // their naming is not part of the FE contract. The semantic state flag
  // (`referrer_bonus_enabled`: true = allowed/unblocked, false = blocked)
  // and the cancellation counts carry everything the toast needs.
  const enabled = response?.referrer_bonus_enabled;
  const cancelled = Number(response?.cancelled_count) || 0;
  const skipped = Number(response?.skipped_count) || 0;

  if (enabled === true) {
    return AppStrings.BLOCK_REFER_A_FRIEND_UNBLOCKED;
  }

  if (enabled === false) {
    if (cancelled === 0 && skipped === 0) {
      return AppStrings.BLOCK_REFER_A_FRIEND_BLOCKED;
    }
    const parts = [];
    if (cancelled > 0) {
      parts.push(
        formatString(AppStrings.BLOCK_REFER_A_FRIEND_CANCELLED_COUNT, {
          count: String(cancelled),
        })
      );
    }
    if (skipped > 0) {
      parts.push(
        formatString(AppStrings.BLOCK_REFER_A_FRIEND_SKIPPED_COUNT, {
          count: String(skipped),
        })
      );
    }
    return `${AppStrings.BLOCK_REFER_A_FRIEND_BLOCKED} (${parts.join(", ")})`;
  }

  return AppStrings.BLOCK_REFER_A_FRIEND_UPDATED;
};

const PersonalLimitsPopup = ({
  isVisible,
  onClosePressed,
  userId,
  hasSportsbookProvider,
  hasCasinoProvider,
  initialUserLimitsLocked = false,
  initialIsPreventFromWithdraw = false,
  initialBonusBlocked = false,
}) => {
  // non-state variables
  const token = JSON.parse(localStorage.getItem("token"));
  const PrivateAdminPanelInstance = createPrivateAdminPanelInstance(
    token?.AccessToken
  );

  // state variables
  const [isApiRunning, setIsApiRunning] = useState(false);
  const [userPersonalLimit, setUserPersonalLimit] = useState({
    max_cash_bonus_per_day: false,
    max_cash_loss_per_day: false,
    max_cash_wagered_per_day: false,
    max_coin_loss_per_day: false,
    max_coin_purchase_per_day: false,
    max_coin_wagered_per_day: false,
    max_playing_time_per_day: false,
    max_playing_time_per_week: false,
    max_casino_cash_wagered_per_day: false,
    max_casino_coin_wagered_per_day: false,
  });

  const [title, setTitle] = useState("Limits");
  const [
    isViewPersonalLimitsPopupVisible,
    setIsViewPersonalLimitsPopupVisible,
  ] = useState(true);
  const [isEditTimeLimitsPopupVisible, setIsEditTimeLimitsPopupVisible] =
    useState(false);
  const [
    isEditCurrencyLimitsPopupVisible,
    setIsEditCurrencyLimitsPopupVisible,
  ] = useState(false);
  const [isEditWaggerLimitsPopupVisible, setIsEditWaggerLimitsPopupVisible] =
    useState(false);
  const [isEditLossLimitsPopupVisible, setIsEditLossLimitsPopupVisible] =
    useState(false);
  const [isEditCasinoLimitsPopupVisible, setIsEditCasinoLimitsPopupVisible] =
    useState(false);
  const [activeTab, setActiveTab] = useState("general");
  const [preEditTab, setPreEditTab] = useState("general");
  const [editingMode, setEditingMode] = useState(false);

  const handleSetEditingMode = (nextEditingMode) => {
    if (nextEditingMode) {
      setPreEditTab(activeTab);
    }
    setEditingMode(nextEditingMode);
  };
  const [userLimitsLocked, setUserLimitsLocked] = useState(initialUserLimitsLocked);
  const [isPreventFromWithdraw, setIsPreventFromWithdraw] = useState(
    Boolean(initialIsPreventFromWithdraw)
  );
  const [bonusBlocked, setBonusBlocked] = useState(
    Boolean(initialBonusBlocked)
  );
  // Toggle UI state — ON means "blocked from receiving referral bonuses".
  // Starts OFF because the BE flag for a never-toggled user reads as
  // `referrer_bonus_enabled: true` (allowed), which inverts to OFF. The
  // GET resolves shortly after open and overrides this when needed.
  const [referralBlocked, setReferralBlocked] = useState(false);

  // In-flight flag for the lock toggle (manual flow). Drives the `disabled`
  // prop of its ToggleSwitch so the user can't double-click while the PATCH
  // is running. The Block Withdrawals and Bonus toggles use the mutation's
  // own `isPending` instead.
  const [isLockLoading, setIsLockLoading] = useState(false);

  // Block Withdrawals reads the current value via a dedicated GET (TanStack
  // Query) every time the popup opens, then writes via PATCH. The prop is
  // the seed value so the toggle doesn't flicker on open while the GET is
  // in flight; the GET is the source of truth and overrides it once it
  // resolves with a boolean payload.
  const { data: withdrawPreventionData } = useGetWithdrawPrevention(
    userId,
    isVisible,
  );
  const {
    mutateAsync: patchWithdrawPrevention,
    isPending: isBlockWithdrawalsLoading,
  } = usePatchWithdrawPrevention();

  // Gate the Bonuses + Block Refer a Friend tabs by the `users` admin
  // permission. They share the same gating today; keeping the variables
  // separate so a future ticket can diverge them without touching every
  // call site.
  const { adminPermissions } = useContext(AppContext) || {};
  const hasUsersAdminPermission =
    Array.isArray(adminPermissions) &&
    adminPermissions.includes("users");
  const canSeeBonusesTab = hasUsersAdminPermission;
  const canSeeReferAFriendTab = hasUsersAdminPermission;
  const { data: bonusEnabledData } = useGetBonusEnabled(
    userId,
    isVisible && canSeeBonusesTab,
  );
  const { mutateAsync: patchBonusEnabled, isPending: isBonusBlockedLoading } =
    usePatchBonusEnabled();

  // Block Refer-a-Friend (PAMENG-1776) mirrors the bonus toggle pattern:
  // dedicated GET on open (gated by the matching permission) + PATCH on
  // toggle. The BE field is `referrer_bonus_enabled` (allow-list flag),
  // so it is **inverted** to the UI toggle ("blocked" = ON).
  const { data: referrerBonusEnabledData } = useGetReferrerBonusEnabled(
    userId,
    isVisible && canSeeReferAFriendTab,
  );
  const {
    mutateAsync: patchReferrerBonusEnabled,
    isPending: isReferralBlockedLoading,
  } = usePatchReferrerBonusEnabled();

  useEffect(() => {
    if (isVisible) {
      setUserLimitsLocked(initialUserLimitsLocked);
    }
  }, [isVisible, initialUserLimitsLocked]);

  useEffect(() => {
    setIsPreventFromWithdraw(Boolean(initialIsPreventFromWithdraw));
  }, [initialIsPreventFromWithdraw]);

  useEffect(() => {
    const value = withdrawPreventionData?.is_prevent_from_withdraw;
    if (typeof value === "boolean") {
      setIsPreventFromWithdraw(value);
    }
  }, [withdrawPreventionData]);

  // Seed the bonus toggle from the parent user payload so it doesn't flicker
  // while the GET is in flight.
  useEffect(() => {
    setBonusBlocked(Boolean(initialBonusBlocked));
  }, [initialBonusBlocked, isVisible]);

  // GET is the source of truth: once it resolves with a boolean `bonus_enabled`
  // we override the seed. `bonus_enabled` is inverted to the "blocked" toggle.
  useEffect(() => {
    const value = bonusEnabledData?.bonus_enabled;
    if (typeof value === "boolean") {
      setBonusBlocked(!value);
    }
  }, [bonusEnabledData]);

  // Reset the referral-block toggle every time the popup opens so a stale
  // state from a previous user/session doesn't leak across openings. The
  // GET below overrides this as soon as it resolves.
  useEffect(() => {
    if (isVisible) {
      setReferralBlocked(false);
    }
  }, [isVisible]);

  // GET is the source of truth: once it resolves with a boolean
  // `referrer_bonus_enabled` we invert it into the UI's "blocked" state.
  // The BE returns `true` for never-toggled users (treated as allowed),
  // so the toggle stays OFF until an operator explicitly blocks them.
  useEffect(() => {
    const value = referrerBonusEnabledData?.referrer_bonus_enabled;
    if (typeof value === "boolean") {
      setReferralBlocked(!value);
    }
  }, [referrerBonusEnabledData]);

  // use effects'

  // api functions

  const getUserPersonalLimit = async (shouldUpdateLockedLimit = false) => {
    if (!userId) return;

    setIsApiRunning(true);

    const params = {
      user_id: userId,
    };

    try {
      const response = await PrivateAdminPanelInstance.get(
        API_ROUTES.USERS_PERSONAL_LIMIT?.replace("{user_id}", params?.user_id),
        params
      );
      const data = response.data.data;

      setUserPersonalLimit(data);
      if (data?.is_user_locked_limit !== undefined) {
        setUserLimitsLocked(Boolean(data?.is_user_locked_limit));
      }
      if (data?.user_limits_locked !== undefined) {
        setUserLimitsLocked(Boolean(data?.user_limits_locked));
      }

      const allKeysAreFalse = Object.values(data).every(
        (value) => value === false
      );

      if (allKeysAreFalse && shouldUpdateLockedLimit) {
        // Auto-lock on empty limits — don't surface a toast for this since
        // the operator didn't initiate the action.
        await lockPersonalLimit(userId, false, { silent: true });
      }

      setIsApiRunning(false);
    } catch (error) {
      console.error("Error getting user personal limits: ", error);
    }

    setIsApiRunning(false);
  };

  // `silent` skips the success/error toasts. Used by the auto-lock flow in
  // `getUserPersonalLimit` so the operator isn't spammed with a toast every
  // time they open the popup of a user whose limits are all zero.
  const lockPersonalLimit = async (userId, lockStatus, { silent = false } = {}) => {
    if (!userId) return;

    if (!silent) setIsLockLoading(true);
    try {
      await PrivateAdminPanelInstance.patch(
        API_ROUTES.UPDATE_USER_LOCKED_LIMIT?.replace("{user_id}", userId),
        { user_limits_locked: lockStatus }
      );
      setUserLimitsLocked(lockStatus);
      if (!silent) {
        message.success(AppStrings.LOCK_USER_LIMITS_UPDATED);
      }
    } catch (error) {
      console.error("Error locking personal limits: ", error);
      if (!silent) {
        message.error(AppStrings.LOCK_USER_LIMITS_UPDATE_FAILED);
      }
    } finally {
      if (!silent) setIsLockLoading(false);
    }
  };

  const handleLockToggle = (payload) => {
    const lockStatus = payload?.is_active ?? false;
    lockPersonalLimit(userId, lockStatus);
  };

  // Writes the `is_prevent_from_withdraw` flag on the user account. The field
  // is owned by the Payments backend and has its own dedicated endpoint.
  // Optimistic update with revert on failure.
  const updatePreventFromWithdraw = async (value) => {
    if (!userId) return;

    const previousValue = isPreventFromWithdraw;
    setIsPreventFromWithdraw(value);

    try {
      await patchWithdrawPrevention({
        user_id: userId,
        is_prevent_from_withdraw: value,
      });
      message.success(AppStrings.BLOCK_WITHDRAWALS_UPDATED);
    } catch (error) {
      console.error("Error updating is_prevent_from_withdraw: ", error);
      setIsPreventFromWithdraw(previousValue);
      message.error(AppStrings.BLOCK_WITHDRAWALS_UPDATE_FAILED);
    }
  };

  const handleBlockWithdrawalsToggle = (payload) => {
    const nextValue = payload?.is_active ?? false;
    updatePreventFromWithdraw(nextValue);
  };

  // Writes the bonus eligibility flag for the user (BONENG-398). The toggle is
  // "blocked", so it PATCHes the inverse `bonus_enabled`. The backend emits a
  // GLI audit event. Optimistic update with revert on failure.
  const updateBonusBlocked = async (value) => {
    if (!userId) return;

    const previousValue = bonusBlocked;
    setBonusBlocked(value);

    try {
      await patchBonusEnabled({
        user_id: userId,
        bonus_enabled: !value,
      });
      message.success(AppStrings.USER_BONUS_BLOCKED_UPDATED);
    } catch (error) {
      console.error("Error updating bonus_enabled: ", error);
      setBonusBlocked(previousValue);
      message.error(AppStrings.USER_BONUS_BLOCKED_UPDATE_FAILED);
    }
  };

  const handleBonusBlockedToggle = (payload) => {
    const nextValue = payload?.is_active ?? false;
    updateBonusBlocked(nextValue);
  };

  // Writes the `referrer_bonus_enabled` flag on the user account (PAMENG-1776).
  // Same optimistic-update-with-revert flow as the bonus toggle. The BE
  // flag is the allow-list version of the UI toggle, so the toggle's
  // "blocked" state is **inverted** on the way to the wire:
  //   UI toggle ON  (blocked)   → PATCH { referrer_bonus_enabled: false }
  //   UI toggle OFF (unblocked) → PATCH { referrer_bonus_enabled: true  }
  //
  // The response carries the updated `referrer_bonus_enabled` flag and
  // (on a successful block) the counts + per-row results of the
  // cancellation step that runs server-side. The user-facing toast is
  // built from that flag and the counts — `msg_code` is deliberately
  // ignored (see buildReferrerBonusToast).
  const updateReferralBlocked = async (value) => {
    if (!userId) return;

    const previousValue = referralBlocked;
    setReferralBlocked(value);

    try {
      const response = await patchReferrerBonusEnabled({
        user_id: userId,
        referrer_bonus_enabled: !value,
      });
      message.success(buildReferrerBonusToast(response));
    } catch (error) {
      console.error("Error updating referrer_bonus_enabled: ", error);
      setReferralBlocked(previousValue);
      message.error(AppStrings.BLOCK_REFER_A_FRIEND_UPDATE_FAILED);
    }
  };

  const handleReferralBlockedToggle = (payload) => {
    const nextValue = payload?.is_active ?? false;
    updateReferralBlocked(nextValue);
  };

  // helper functions
  const showPersonalLimits = () => {
    setIsViewPersonalLimitsPopupVisible(true);
    setIsEditTimeLimitsPopupVisible(false);
    setIsEditCurrencyLimitsPopupVisible(false);
    setIsEditWaggerLimitsPopupVisible(false);
    setIsEditLossLimitsPopupVisible(false);
    setIsEditCasinoLimitsPopupVisible(false);
    setEditingMode(false);
    setActiveTab(preEditTab);
    setTitle("Limits");
  };
  const handleClosePressed = () => {
    showPersonalLimits();
    onClosePressed();
  };

  if (!isVisible) return null;

  return (
    <div className="personal-limits-modal-container">
      <div className="modal-header">
        <div className="modal-header-title">
          <h2 className="modal-title">{title}</h2>
        </div>
        <CloseOutlined onClick={handleClosePressed} className="close-icon" />
      </div>
      <div className="modal-content">
        {!editingMode && (
          <div className="personal-limits-lock-row">
            <span className="personal-limits-lock-label">
              {AppStrings.LOCK_USER_LIMITS}
            </span>
            <ToggleSwitch
              status={userLimitsLocked}
              eventCode="lock-user-limits"
              updateEventCode={handleLockToggle}
              onlyUpdate={false}
              disabled={isLockLoading}
            />
          </div>
        )}
        {!editingMode && (hasCasinoProvider || hasSportsbookProvider) && (
          <div className="personal-limits-tabs">
            <div
              className={`personal-limits-tab ${activeTab === "general" ? "active" : ""
                }`}
              onClick={() => setActiveTab("general")}
            >
              {AppStrings.PAM_GENERAL}
            </div>
            {hasSportsbookProvider && (
              <div
                className={`personal-limits-tab ${activeTab === "sportsbook" ? "active" : ""
                  }`}
                onClick={() => setActiveTab("sportsbook")}
              >
                {AppStrings.SPORTSBOOK}
              </div>
            )}
            {hasCasinoProvider && (
              <div
                className={`personal-limits-tab ${activeTab === "casino" ? "active" : ""
                  }`}
                onClick={() => setActiveTab("casino")}
              >
                {AppStrings.CASINO}
              </div>
            )}
            <div
              className={`personal-limits-tab ${activeTab === "payments" ? "active" : ""
                }`}
              onClick={() => setActiveTab("payments")}
            >
              {AppStrings.PAYMENTS}
            </div>
              {canSeeBonusesTab && (
                <div
                  className={`personal-limits-tab ${activeTab === "bonuses" ? "active" : ""
                    }`}
                  onClick={() => setActiveTab("bonuses")}
                >
                  {AppStrings.BONUSES}
                </div>
              )}
              {canSeeReferAFriendTab && (
                <div
                  className={`personal-limits-tab ${activeTab === "refer_a_friend" ? "active" : ""
                    }`}
                  onClick={() => setActiveTab("refer_a_friend")}
                >
                  {AppStrings.BLOCK_REFER_A_FRIEND}
                </div>
              )}
          </div>
        )}
        {activeTab === "general" && (
          <>
            {isEditTimeLimitsPopupVisible && (
              <EditTimeLimitsPopup
                getUserPersonalLimit={getUserPersonalLimit}
                userId={userId}
                limits={{
                  timeAmount: userPersonalLimit.max_playing_time_per_day,
                  daysPerWeek: userPersonalLimit.max_playing_time_per_week,
                }}
                showPersonalLimits={showPersonalLimits}
              />
            )}
            {isEditCurrencyLimitsPopupVisible && (
              <EditCurrencyLimitsPopup
                getUserPersonalLimit={getUserPersonalLimit}
                userId={userId}
                limits={{
                  rebetCash: userPersonalLimit.max_cash_bonus_per_day,
                  rebetCoins: userPersonalLimit.max_coin_purchase_per_day,
                }}
                showPersonalLimits={showPersonalLimits}
              />
            )}
            {isViewPersonalLimitsPopupVisible && (
              <PersonalLimits
                getUserPersonalLimit={getUserPersonalLimit}
                userId={userId}
                userPersonalLimit={userPersonalLimit}
                setTitle={setTitle}
                setIsViewPersonalLimitsPopupVisible={
                  setIsViewPersonalLimitsPopupVisible
                }
                setIsEditTimeLimitsPopupVisible={
                  setIsEditTimeLimitsPopupVisible
                }
                setIsEditCurrencyLimitsPopupVisible={
                  setIsEditCurrencyLimitsPopupVisible
                }
                isApiRunning={isApiRunning}
                setEditingMode={handleSetEditingMode}
              />
            )}
          </>
        )}
        {activeTab === "sportsbook" && hasSportsbookProvider && (
          <>
            {isEditWaggerLimitsPopupVisible && (
              <EditWaggerLimitsPopup
                getUserPersonalLimit={getUserPersonalLimit}
                userId={userId}
                limits={{
                  rebetCash: userPersonalLimit.max_cash_wagered_per_day,
                  rebetCoins: userPersonalLimit.max_coin_wagered_per_day,
                }}
                showPersonalLimits={showPersonalLimits}
              />
            )}
            {isEditLossLimitsPopupVisible && (
              <EditLossLimitsPopup
                getUserPersonalLimit={getUserPersonalLimit}
                userId={userId}
                limits={{
                  rebetCash: userPersonalLimit.max_cash_loss_per_day,
                  rebetCoins: userPersonalLimit.max_coin_loss_per_day,
                }}
                showPersonalLimits={showPersonalLimits}
              />
            )}
            {isViewPersonalLimitsPopupVisible && (
              <SportsbookLimits
                getUserPersonalLimit={getUserPersonalLimit}
                userPersonalLimit={userPersonalLimit}
                setTitle={setTitle}
                setIsViewPersonalLimitsPopupVisible={
                  setIsViewPersonalLimitsPopupVisible
                }
                setIsEditWaggerLimitsPopupVisible={
                  setIsEditWaggerLimitsPopupVisible
                }
                setIsEditLossLimitsPopupVisible={
                  setIsEditLossLimitsPopupVisible
                }
                isApiRunning={isApiRunning}
                setEditingMode={handleSetEditingMode}
              />
            )}
          </>
        )}
        {activeTab === "casino" && hasCasinoProvider && (
          <>
            {isEditCasinoLimitsPopupVisible && (
              <EditCasinoLimitsPopup
                getUserPersonalLimit={getUserPersonalLimit}
                userId={userId}
                limits={{
                  rebetCash:
                    userPersonalLimit?.max_casino_cash_wagered_per_day ?? 0,
                  rebetCoins:
                    userPersonalLimit?.max_casino_coin_wagered_per_day ?? 0,
                }}
                showPersonalLimits={showPersonalLimits}
              />
            )}
            {isViewPersonalLimitsPopupVisible && (
              <CasinoLimits
                getUserPersonalLimit={getUserPersonalLimit}
                setTitle={setTitle}
                userPersonalLimit={userPersonalLimit}
                setIsViewPersonalLimitsPopupVisible={
                  setIsViewPersonalLimitsPopupVisible
                }
                setIsEditCasinoLimitsPopupVisible={
                  setIsEditCasinoLimitsPopupVisible
                }
                isApiRunning={isApiRunning}
                setEditingMode={handleSetEditingMode}
              />
            )}
          </>
        )}
        {activeTab === "payments" && (
          <div className="personal-limits-payments-section">
            <div className="personal-limits-lock-row">
              <span className="personal-limits-lock-label">
                {AppStrings.BLOCK_WITHDRAWALS}
              </span>
              <ToggleSwitch
                status={isPreventFromWithdraw}
                eventCode="block-withdrawals"
                updateEventCode={handleBlockWithdrawalsToggle}
                onlyUpdate={false}
                disabled={isBlockWithdrawalsLoading}
              />
            </div>
          </div>
        )}
        {canSeeBonusesTab && activeTab === "bonuses" && (
          <div className="personal-limits-bonuses-section">
            <div className="personal-limits-lock-row">
              <span className="personal-limits-lock-label">
                {AppStrings.USER_BONUS_BLOCKED}
              </span>
              <ToggleSwitch
                status={bonusBlocked}
                eventCode="user-bonus-blocked"
                updateEventCode={handleBonusBlockedToggle}
                onlyUpdate={false}
                disabled={isBonusBlockedLoading}
              />
            </div>
          </div>
        )}
        {canSeeReferAFriendTab && activeTab === "refer_a_friend" && (
          <div className="personal-limits-refer-a-friend-section">
            <div className="personal-limits-lock-row">
              <span className="personal-limits-lock-label">
                {AppStrings.BLOCK_REFER_A_FRIEND}
              </span>
              <ToggleSwitch
                status={referralBlocked}
                eventCode="block-refer-a-friend"
                updateEventCode={handleReferralBlockedToggle}
                onlyUpdate={false}
                disabled={isReferralBlockedLoading}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PersonalLimitsPopup;
